import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { FieldConfigurationViewRenderer } from '@client/components/_ui/data-entry/FieldConfigurationRenderer';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { QueryState } from '@client/components/_ui/feedback/QueryState';
import { LodgementSubmissionDrawer } from '@client/components/lodgement/submissions/LodgementSubmissionDrawer';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { ICONS } from '@client/styles/theme';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Col, Row, Tag } from 'antd';
import { History, Inbox, Lock } from 'lucide-react';
import { useState } from 'react';
import { claimFormStore } from '../store';
import {
    describeFormCoverage,
    describeLockedFields,
    describeReadOnlyReason,
    type FormMappedFieldIds,
} from './product-fields-provenance';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/product-fields')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductFields />
        </PageController>
    );
}

/**
 * The claim's product fields: what lodgement v2 captured for the version plus
 * any handler fields, in the order the admin configured. A field is editable
 * here only when its configuration says so; the rest keep the lodged answer.
 */
function ProductFields() {
    const { orgId, id } = Route.useParams();
    const claim = claimFormStore.use.formData();
    const isReadonly = claimFormStore.use.isReadonly();
    const { formatDate } = useFormatter();
    const [openSubmissionId, setOpenSubmissionId] = useState<bigint | null>(null);

    const fieldsQuery = trpc.fieldConfiguration.listFieldConfigurations.useQuery(
        claim.productVersionId ? { where: { entity: 'PRODUCT_VERSION', entityId: claim.productVersionId } } : skipToken,
        { refetchOnWindowFocus: false, meta: { errorSurface: 'inline' } },
    );
    const { data } = fieldsQuery;
    const { productVersionId } = claim;

    // Where the claim came from (CUR-1462). Secondary to the fields: while it loads or when it
    // fails the header keeps the plain line and the read-only copy stays neutral. Not sent to
    // Sentry: the tab is flagless, so an org with products v2 switched off answers NOT_FOUND on
    // every view. A lodged claim's provenance does not change, so it is cached across tab visits.
    const provenanceQuery = trpc.lodgementSubmission.getClaimProvenance.useQuery(
        claim.productVersionId ? { claimId: claim.id } : skipToken,
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60_000,
            meta: { errorSurface: 'silent', captureToSentry: false },
        },
    );
    const submission = provenanceQuery.data?.submission ?? null;

    const values = claim.productVersionCustomFieldsData ?? {};
    const hasValue = (fieldId: string) => {
        const value = values[fieldId];
        return value !== null && value !== undefined && value !== '';
    };
    // Mirrors the renderer: active fields always, archived ones only while they hold a value.
    const shownFields = (data?.rows ?? []).filter((field) => !field.isArchived || hasValue(field.fieldId));
    // The fields the lodged answers filled, worked out on the server from the stored answers, so a
    // value a handler types here (saved or not) is never credited to the form.
    const formMappedIds: FormMappedFieldIds = provenanceQuery.data
        ? new Set(provenanceQuery.data.formFilledFieldConfigurationIds)
        : null;
    const lockedFieldsNote = provenanceQuery.isLoading ? null : describeLockedFields(shownFields, formMappedIds);
    const formCoverage = describeFormCoverage(shownFields, formMappedIds);
    const version = claim.ProductVersion;
    const lodgedAt = submission?.submittedAt ?? claim.claimReportedAt;
    const noFieldsEmptyState = (
        <EmptyState
            size="small"
            icon={<ICONS.productType />}
            title="No product fields to show"
            description="Add them on the product version’s Fields tab; values lodged for archived fields stay in History."
        />
    );

    return (
        <>
            <Row gutter={[0, 16]}>
                <Col span={16}>
                    <Card
                        size="small"
                        title={
                            <div className="flex min-w-0 flex-col gap-0.5 py-1">
                                <div className="flex items-center gap-2">
                                    <span className="truncate">{version?.Product.name ?? 'Product fields'}</span>
                                    {version && <Tag className="m-0 shrink-0">Version {version.version}</Tag>}
                                </div>
                                {submission ? (
                                    <div className="flex flex-wrap items-center gap-x-1.5 font-normal text-subtle-foreground text-xs">
                                        {version?.name && (
                                            <>
                                                <span>{version.name}</span>
                                                <span aria-hidden="true">·</span>
                                            </>
                                        )}
                                        <span>
                                            {lodgedAt && `Lodged ${formatDate(lodgedAt)} `}via{' '}
                                            <span className="font-medium text-muted-foreground">
                                                {submission.link.name}
                                            </span>
                                        </span>
                                        <span aria-hidden="true">·</span>
                                        <span>
                                            form{' '}
                                            <span className="font-medium text-muted-foreground">
                                                {submission.form.name} v{submission.form.version}
                                            </span>
                                        </span>
                                        {formCoverage && (
                                            <>
                                                <span aria-hidden="true">·</span>
                                                <span>{formCoverage}</span>
                                            </>
                                        )}
                                    </div>
                                ) : (
                                    <div className="font-normal text-subtle-foreground text-xs">
                                        {[version?.name, lodgedAt && `Lodged ${formatDate(lodgedAt)}`]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </div>
                                )}
                            </div>
                        }
                        extra={
                            <div className="flex items-center gap-1">
                                {submission?.canOpen && (
                                    <Button
                                        type="link"
                                        icon={<Inbox size={16} />}
                                        onClick={() => setOpenSubmissionId(submission.id)}
                                    >
                                        Submission
                                    </Button>
                                )}
                                <Button.Link
                                    variant="link"
                                    icon={<History size={16} />}
                                    to="/console/$orgId/claims/details/$id/history"
                                    params={{ orgId, id }}
                                >
                                    History
                                </Button.Link>
                            </div>
                        }
                    >
                        {productVersionId ? (
                            <QueryState
                                query={fieldsQuery}
                                compact
                                isEmpty={() => shownFields.length === 0}
                                empty={noFieldsEmptyState}
                            >
                                {() => (
                                    <Row gutter={[16, 16]}>
                                        <FieldConfigurationViewRenderer
                                            entity="PRODUCT_VERSION"
                                            entityId={productVersionId}
                                            section={null}
                                            fieldPathPrefix="productVersionCustomFieldsData"
                                            formStore={claimFormStore}
                                            isReadOnly={isReadonly}
                                            fieldReadOnlyReason={(field) =>
                                                describeReadOnlyReason(field, formMappedIds)
                                            }
                                        />
                                    </Row>
                                )}
                            </QueryState>
                        ) : (
                            noFieldsEmptyState
                        )}
                        {lockedFieldsNote && (
                            <div className="mt-4 flex items-center gap-2 border-separator-secondary border-t pt-3 text-subtle-foreground text-xs">
                                <Lock size={13} className="shrink-0" />
                                <span>{lockedFieldsNote}</span>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
            <LodgementSubmissionDrawer
                submissionId={openSubmissionId}
                onClose={() => setOpenSubmissionId(null)}
                linkToClaim={false}
            />
        </>
    );
}
